import React, { useState } from 'react';
import { apiFetch, parseJsonSafe } from '../utils/api';

const fmtBytes = (size) => (size ? `${Math.round((Number(size) / 1024) * 10) / 10} KB` : '');
const canPreview = (mime = '') => {
  const m = String(mime || '').toLowerCase();
  return m.startsWith('image/') || m === 'application/pdf' || m.startsWith('text/');
};

// Gestión de adjuntos de una tarea: subida múltiple, descarga, eliminación y
// previsualización en modal (imágenes/PDF/texto). Autocontenido y reutilizable.
const TaskAttachments = ({ taskId, attachments = [], token, onChanged, onUnauthorized }) => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState('');
  const [err, setErr] = useState('');
  const [inputKey, setInputKey] = useState(0);
  const [visBusyId, setVisBusyId] = useState(null);
  const [preview, setPreview] = useState({ open: false, url: '', mime: '', filename: '', downloadUrl: '', loading: false });

  const headers = { Authorization: `Bearer ${token}` };
  const handle401 = () => { if (onUnauthorized) onUnauthorized(); };
  const list = Array.isArray(attachments) ? attachments : [];

  const toggleVisible = async (att) => {
    setVisBusyId(att.id);
    try {
      const res = await apiFetch(`/api/tasks/task-attachments/${att.id}/visibility`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ visible_cliente: !att.visible_cliente })
      });
      if (res.status === 401) return handle401();
      if (!res.ok) { const d = await parseJsonSafe(res); throw new Error((d && d.message) || 'No se pudo actualizar la visibilidad'); }
      if (onChanged) await onChanged();
    } catch (e) {
      alert(e.message);
    } finally {
      setVisBusyId(null);
    }
  };

  const downloadFile = async (url, filename) => {
    try {
      const res = await apiFetch(url, { headers });
      if (res.status === 401) return handle401();
      if (!res.ok) { const d = await parseJsonSafe(res); throw new Error((d && d.message) || 'Error descargando archivo'); }
      const blob = await res.blob();
      const u = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = u;
      a.download = filename || 'archivo';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(u);
    } catch (e) {
      alert(e.message);
    }
  };

  const openPreview = async (att) => {
    const downloadUrl = `/api/tasks/task-attachments/${att.id}/download`;
    setPreview({ open: true, url: '', mime: att.mime_type || '', filename: att.filename, downloadUrl, loading: true });
    try {
      const res = await apiFetch(downloadUrl, { headers });
      if (res.status === 401) return handle401();
      if (!res.ok) { const d = await parseJsonSafe(res); throw new Error((d && d.message) || 'No se pudo cargar la vista previa'); }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      setPreview((p) => ({ ...p, url, loading: false }));
    } catch (e) {
      setPreview({ open: false, url: '', mime: '', filename: '', downloadUrl: '', loading: false });
      alert(e.message);
    }
  };

  const closePreview = () => setPreview((p) => {
    if (p.url) window.URL.revokeObjectURL(p.url);
    return { open: false, url: '', mime: '', filename: '', downloadUrl: '', loading: false };
  });

  const upload = async () => {
    if (!files.length) return;
    setUploading(true);
    setErr('');
    const errores = [];
    try {
      for (let i = 0; i < files.length; i += 1) {
        const file = files[i];
        setProgress(`Subiendo ${i + 1}/${files.length}...`);
        try {
          const fd = new FormData();
          fd.append('file', file);
          const res = await apiFetch(`/api/tasks/${taskId}/attachments`, { method: 'POST', headers, body: fd });
          if (res.status === 401) return handle401();
          if (!res.ok) { const d = await parseJsonSafe(res); throw new Error((d && d.message) || 'No se pudo subir'); }
        } catch (e) {
          errores.push(`${file.name}: ${e.message}`);
        }
      }
      setFiles([]);
      setInputKey((k) => k + 1);
      if (errores.length) setErr(`No se pudieron subir ${errores.length} archivo(s): ${errores.join('; ')}`);
      if (onChanged) await onChanged();
    } finally {
      setUploading(false);
      setProgress('');
    }
  };

  const remove = async (attId) => {
    if (!window.confirm('¿Eliminar este adjunto?')) return;
    try {
      const res = await apiFetch(`/api/tasks/task-attachments/${attId}`, { method: 'DELETE', headers });
      if (res.status === 401) return handle401();
      if (!res.ok) { const d = await parseJsonSafe(res); throw new Error((d && d.message) || 'No se pudo eliminar'); }
      if (onChanged) await onChanged();
    } catch (e) {
      alert(e.message);
    }
  };

  return (
    <div className="mt-2 pt-2 border-top">
      <div className="text-muted small mb-1">Adjuntos</div>
      {list.length > 0 ? (
        <ul className="list-unstyled mb-2">
          {list.map((a) => (
            <li key={a.id} className="d-flex justify-content-between align-items-center py-1">
              <span className="text-truncate me-2">
                {a.filename} <span className="text-muted small">· {fmtBytes(a.size)}</span>
              </span>
              <span className="d-flex align-items-center gap-2 flex-shrink-0">
                <span className="form-check form-switch m-0" title="Visible en el área de cliente">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    id={`tavis-${a.id}`}
                    checked={a.visible_cliente === true}
                    disabled={visBusyId === a.id}
                    onChange={() => toggleVisible(a)}
                  />
                  <label className="form-check-label small text-muted" htmlFor={`tavis-${a.id}`}>Área cliente</label>
                </span>
                <span className="btn-group btn-group-sm">
                  {canPreview(a.mime_type) && (
                    <button className="btn btn-outline-secondary" type="button" onClick={() => openPreview(a)}>Ver</button>
                  )}
                  <button
                    className="btn btn-outline-primary"
                    type="button"
                    onClick={() => downloadFile(`/api/tasks/task-attachments/${a.id}/download`, a.filename)}
                  >
                    Descargar
                  </button>
                  <button className="btn btn-outline-danger" type="button" onClick={() => remove(a.id)}>Eliminar</button>
                </span>
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <div className="text-muted small mb-2">Sin adjuntos.</div>
      )}
      <div className="d-flex gap-2 align-items-center">
        <input
          key={inputKey}
          type="file"
          multiple
          className="form-control form-control-sm"
          onChange={(e) => setFiles(Array.from(e.target.files || []))}
        />
        <button className="btn btn-sm btn-outline-primary flex-shrink-0" type="button" disabled={!files.length || uploading} onClick={upload}>
          {uploading ? (progress || 'Subiendo...') : (files.length > 1 ? `Adjuntar ${files.length}` : 'Adjuntar')}
        </button>
      </div>
      {err && <div className="text-danger small mt-1">{err}</div>}

      {preview.open && (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.6)' }} onClick={closePreview}>
          <div className="modal-dialog modal-xl modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title text-truncate" title={preview.filename}>{preview.filename}</h5>
                <button className="btn-close" type="button" onClick={closePreview}></button>
              </div>
              <div className="modal-body text-center" style={{ minHeight: 220 }}>
                {preview.loading && <div className="text-muted py-4">Cargando vista previa...</div>}
                {!preview.loading && preview.url && (
                  (preview.mime || '').toLowerCase().startsWith('image/')
                    ? <img src={preview.url} alt={preview.filename} style={{ maxWidth: '100%', maxHeight: '75vh', objectFit: 'contain' }} />
                    : <iframe title={preview.filename} src={preview.url} style={{ width: '100%', height: '75vh', border: 'none' }} />
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-primary" type="button" onClick={() => downloadFile(preview.downloadUrl, preview.filename)}>Descargar</button>
                <button className="btn btn-outline-secondary" type="button" onClick={closePreview}>Cerrar</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskAttachments;
